import React, { Component } from 'react';
import memoizeOne from 'memoize-one';
import PropTypes from 'prop-types';
import cx from 'classnames';
import isEqual from 'react-fast-compare';
import * as Rx from 'rxjs';
import { map, scan, distinctUntilChanged } from 'rxjs/operators';
import { Button, Intent, Spinner } from '@blueprintjs/core';
import _ from 'lodash';
import {
  standardDrawing,
  alignFlip,
  setPositions,
  setUnchangedPositions,
  calculateCuts,
  applyCutsToDrawing,
  extractMolEvent,
} from '@moleculeone/draw';
import { isMacOS, V4 } from '@moleculeone/utils';

import { smilesToMol } from '@f/utils/conversion';
import { calculateSeededPositions } from '@f/utils/mol';

import Toolbox from '../molecule/toolbox/Toolbox';
import DrawnMolecule from '../molecule/DrawnMolecule';
import { PriceSidebar } from '../price-list/PriceSidebar';
import { PriceQuery } from '../price-query/PriceQuery';

import cls from './Reaction.module.css';

const substrateColors = ['#007bff', '#28a745', '#dc3545', '#ffc107'];
const hoverCircleColor = substrateColors[3];

const HOVER_ALL = 'HOVER_ALL';
const HOVER_MOL = 'HOVER_MOL';
const HOVER_ATOM = 'HOVER_ATOM';
const HOVER_NONE = 'HOVER_NONE';

const REACTION_SUBSTRATE = 'REACTION_SUBSTRATE';
const REACTION_PRODUCT = 'REACTION_PRODUCT';

let timer = null;

export default class Reaction extends Component {
  static propTypes = {
    onClickProduct: PropTypes.func,
    onClickSubstrate: PropTypes.func,
    product: PropTypes.shape({
      key: PropTypes.any,
      smiles: PropTypes.string.isRequired,
    }).isRequired,
    substrates: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.any,
        smiles: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
    productToMap: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
    substratesToMap: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number.isRequired).isRequired)
      .isRequired,
    highlights: PropTypes.arrayOf(PropTypes.any).isRequired,
    toolbox: PropTypes.bool,
    className: PropTypes.string,
    reagentClassName: PropTypes.string,
    compact: PropTypes.bool,
    async: PropTypes.bool,
    drawCuts: PropTypes.bool,
    drawSubstrates: PropTypes.bool,
    reactionArrowType: PropTypes.oneOf(['single', 'multiple']),
    steps: PropTypes.number,
    showPrices: PropTypes.bool,
  };

  static defaultProps = {
    onClickProduct: () => {},
    onClickSubstrate: () => {},
    toolbox: true,
    compact: false,
    async: true,
    drawCuts: false,
    drawSubstrates: true,
    reactionArrowType: 'single',
    steps: 1,
    showPrices: false,
  };

  static mapPurely = (product, substrates, productToMap, substratesToMap) => {
    const mapToSubstrates = substratesToMap.reduce((result, substrateMap, subId) => {
      substrateMap.forEach((atom, atomId) => {
        result[atom] = [subId, atomId];
      });
      return result;
    }, {});

    const mapToProduct = productToMap.reduce((result, atom, atomId) => {
      result[atom] = atomId;
      return result;
    }, {});

    return {
      mapToSubstrates,
      mapToProduct,
    };
  };

  state = {
    productMol: null,
    substrateMols: [],
    drawnProductMol: null,
    drawnSubstrateMols: [],
    hover: {
      type: HOVER_NONE,
      mapId: null,
      substrateIndex: null,
    },
    priceMap: {},
    priceSidebar: {
      isOpen: false,
      smiles: '',
    },
  };

  inputStream = null;

  inputSubscription = null;

  listener = null;

  memoizedMap = memoizeOne(Reaction.mapPurely);

  async componentDidMount() {
    const inputStateAccumulatorSeed = {
      ctrl: false,
      shift: false,
      alt: false,
      hover: {
        substrateIndex: null,
        mapId: null,
      },
    };

    this.inputStream = new Rx.Subject();
    this.inputSubscription = this.inputStream
      .pipe(
        map(e => {
          const modifiers = {
            ctrl: (e.type !== 'keyup' && e.ctrlKey) || (e.metaKey && isMacOS()),
            shift: e.type !== 'keyup' && e.shiftKey,
            alt: e.type !== 'keyup' && e.altKey,
          };

          if (e.type === 'mousemove') {
            modifiers.hover = this.hoverEventData(e);
          }

          return modifiers;
        }),
        scan((acc, nxt) => ({ ...acc, ...nxt }), inputStateAccumulatorSeed),
        map(({ ctrl, shift, alt, hover: { mapId, substrateIndex } }) => {
          if (alt && substrateIndex != null && mapId != null) {
            return { type: HOVER_ATOM, substrateIndex, mapId };
          }
          if (ctrl && substrateIndex != null) {
            return { type: HOVER_MOL, substrateIndex };
          }
          if (shift && mapId != null) {
            return { type: HOVER_ALL };
          }
          return { type: HOVER_NONE };
        }),
        distinctUntilChanged(
          (a, b) =>
            a.type === b.type && a.mapId === b.mapId && a.substrateIndex === b.substrateIndex
        )
      )
      .subscribe(hover => this.setState({ hover }));

    this.listener = e => this.inputStream.next(e);
    document.addEventListener('mousemove', this.listener);
    document.addEventListener('keydown', this.listener);
    document.addEventListener('keyup', this.listener);

    if (this.props.async) {
      timer = setTimeout(() => {
        this.initialiseMols();
      }, 100);
    } else {
      this.initialiseMols();
    }
  }

  // shouldComponentUpdate(nextProps, nextState) {
  //   return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  // }

  async componentDidUpdate(prevProps, prevState) {
    const { product, substrates } = this.props;
    const { product: prevProduct, substrates: prevSubstrates } = prevProps;
    const { hover } = this.state;
    const { hover: prevHover } = prevState;

    const didProductChange = product && product.smiles !== prevProduct.smiles;
    const didSubstratesChange = substrates && !isEqual(substrates, prevSubstrates);
    const didHoverChange = !isEqual(hover, prevHover);

    if (didProductChange || didSubstratesChange) {
      this.initialiseMols();
    } else if (didHoverChange) {
      this.drawMols();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.listener);
    document.removeEventListener('keydown', this.listener);
    document.removeEventListener('keyup', this.listener);
    this.listener = null;
    this.inputStream = null;
    this.inputSubscription.unsubscribe();
    this.inputSubscription = null;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  shouldRenderMolecule(mol) {
    return Boolean(mol instanceof Object);
  }

  mapping() {
    const { product, substrates, productToMap, substratesToMap } = this.props;
    return this.memoizedMap(product, substrates, productToMap, substratesToMap);
  }

  initialiseMols = async () => {
    await this.convertSmilesToMols();
    this.calculateMols();
  };

  convertSmilesToMols = async () => {
    const { product, substrates } = this.props;

    const productMol = await smilesToMol(product.smiles);

    const substrateMols = await Promise.all(
      substrates.map(substrate => {
        return smilesToMol(substrate.smiles);
      })
    );

    this.setState({
      productMol,
      substrateMols,
    });
  };

  drawMols = () => {
    const { productToMap, drawCuts, drawSubstrates } = this.props;
    const { productMol, substrateMols } = this.state;
    const { mapToSubstrates } = this.mapping();

    if (!productMol) {
      return null;
    }

    const { buildProductDrawing, buildSubstrateDrawing } = this.molDrawers();

    const drawnProductMol = buildProductDrawing(productMol);

    let drawnSubstrateMols = [];

    if (drawSubstrates) {
      drawnSubstrateMols = substrateMols.map((substrateMol, idx) => {
        return buildSubstrateDrawing(substrateMol, idx);
      });
    }

    if (drawCuts) {
      const cuts = calculateCuts(productMol, substrateMols, productToMap, mapToSubstrates);
      applyCutsToDrawing(drawnProductMol, cuts);
    }

    return {
      drawnProductMol,
      drawnSubstrateMols,
    };
  };

  calculateMols = () => {
    const { product, substrates } = this.props;
    const { substratesToMap } = this.props;
    const { productMol, substrateMols } = this.state;

    if (!productMol) {
      return;
    }

    const nextProductMol = setPositions(
      productMol,
      calculateSeededPositions({
        mol: productMol,
        seedId: product.smiles,
      })
    );

    // Aligns mols
    const nextSubstrateMols = substrateMols.map((substrateMol, idx) => {
      const map = substratesToMap[idx];
      const intermediateSubstrateMol = setPositions(
        substrateMol,
        calculateSeededPositions({
          mol: substrateMol,
          seedId: substrates[idx].smiles,
        })
      );
      return this.alignSubstrateToProduct(nextProductMol, intermediateSubstrateMol, map);
    });

    this.setState({
      productMol: nextProductMol,
      substrateMols: nextSubstrateMols,
    });
  };

  recalculateProduct = () => {
    const { product } = this.props;
    const { productMol } = this.state;

    const nextProductMol = setPositions(
      productMol,
      calculateSeededPositions({
        mol: productMol,
        seedId: product.smiles,
        incrementSeed: true,
      })
    );

    this.setState({
      productMol: nextProductMol,
    });
  };

  recalculateSubstrate = index => () => {
    const { substrates } = this.props;
    const { substrateMols } = this.state;

    const smiles = substrates[index].smiles;
    const mol = substrateMols[index];

    const nextMol = setPositions(
      mol,
      calculateSeededPositions({
        mol,
        seedId: smiles,
        incrementSeed: true,
      })
    );

    this.setState({
      substrateMols: [...substrateMols.slice(0, index), nextMol, ...substrateMols.slice(index + 1)],
    });
  };

  molDrawers() {
    const { hover } = this.state;

    switch (hover.type) {
      case HOVER_NONE:
        return this.plainMolDrawers();
      case HOVER_ALL:
      case HOVER_ATOM: {
        const colorMap = this.filteredColorMap(() => true);
        return this.colorMapMolDrawer(colorMap);
      }
      case HOVER_MOL: {
        const hoveredIndex = hover.substrateIndex;
        const colorMap = this.filteredColorMap(
          (mapId, substrateId) => substrateId === hoveredIndex
        );
        return this.colorMapMolDrawer(colorMap);
      }
      default:
        throw new Error();
    }
  }

  filteredColorMap(shouldColor) {
    const { mapToSubstrates } = this.mapping();
    const classToColor = {};
    for (const [mapId, [substrateIndex]] of Object.entries(mapToSubstrates)) {
      const should = shouldColor(mapId, substrateIndex);
      classToColor[mapId] = should ? substrateColors[substrateIndex % 4] : 'black';
    }
    return classToColor;
  }

  plainMolDrawers() {
    const { mapToSubstrates } = this.mapping();
    const buildProductDrawing = mol => standardDrawing(mol);

    // Substrate drawing function
    const buildSubstrateDrawing = (mol, substrateIndex) => {
      const selected = this.props.highlights
        .flatMap(([_highlightName, mapIds]) => mapIds)
        .map(cls => mapToSubstrates[cls])
        .filter(x => x != null)
        .filter(([classSubstrateIndex, _atomIndex]) => classSubstrateIndex === substrateIndex)
        .map(([_clsIndex, atomIndex]) => atomIndex);

      return standardDrawing(mol, selected);
    };

    return {
      buildProductDrawing,
      buildSubstrateDrawing,
    };
  }

  colorMapMolDrawer(classToColor) {
    const { productToMap, substratesToMap } = this.props;

    const buildProductDrawing = mol => {
      const drawnMol = standardDrawing(mol);
      for (const [key, atom] of Object.entries(drawnMol.atoms)) {
        const mapId = productToMap[key];
        atom.color = mapId == null ? 'black' : classToColor[mapId];
      }
      return drawnMol;
    };

    const buildSubstrateDrawing = (mol, substrIndex) => {
      const drawnMol = standardDrawing(mol);
      for (const [key, atom] of Object.entries(drawnMol.atoms)) {
        const mapId = substratesToMap[substrIndex][key];
        atom.color = mapId == null ? 'black' : classToColor[mapId];
      }
      return drawnMol;
    };

    return {
      buildProductDrawing,
      buildSubstrateDrawing,
    };
  }

  hoverCircles() {
    const { mapToProduct, mapToSubstrates } = this.mapping();
    const { productMol, substrateMols, hover } = this.state;

    const result = {
      substrateCircle: null,
      productCircle: null,
    };

    const { substrateIndex, mapId, type } = hover;
    const substrateMol = substrateMols[substrateIndex];

    if (type !== HOVER_ATOM || mapId == null) {
      return result;
    }

    if (productMol == null || substrateMol == null) {
      return result;
    }

    const productAtomId = mapToProduct[mapId];
    if (productAtomId != null) {
      const productPos = productMol.positions[productAtomId];
      result.productCircle = (
        <circle
          cx={productPos[0]}
          cy={productPos[1]}
          r={40}
          fill={hoverCircleColor}
          opacity={0.6}
        />
      );
    }

    const substrateLocation = mapToSubstrates[mapId];
    if (substrateLocation != null) {
      const substratePos = substrateMol.positions[substrateLocation[1]];
      result.substrateCircle = (
        <circle
          cx={substratePos[0]}
          cy={substratePos[1]}
          r={40}
          fill={hoverCircleColor}
          opacity={0.6}
        />
      );
    }

    return result;
  }

  alignSubstrateToProduct(productMol, substrateMol, substrateToMap) {
    if (!productMol) {
      return substrateMol;
    }

    const { mapToProduct } = this.mapping();
    const alignMap = {};

    for (const [substrateAtomId, mapId] of Object.entries(substrateToMap)) {
      const productAtomId = mapToProduct[mapId];
      alignMap[substrateAtomId] = productMol.positions[productAtomId];
    }

    return setUnchangedPositions(substrateMol, alignFlip(substrateMol.positions, alignMap));
  }

  hoverEventData(e) {
    const { productToMap, substratesToMap } = this.props;
    const { mapToProduct, mapToSubstrates } = this.mapping();
    const { productMol, substrateMols } = this.state;

    const result = {
      substrateIndex: null,
      mapId: null,
    };

    const substrElem = e.target.closest(`.${REACTION_SUBSTRATE}`);
    const prodElem = e.target.closest(`.${REACTION_PRODUCT}`);

    let hoveredMol = null;
    let indexToMap = null;
    let mapToOtherSide = null;

    if (this.ref && !this.ref.contains(e.target)) {
      return result;
    }
    if (substrElem !== null) {
      const substrateId = parseInt(substrElem.getAttribute('data-substrate-index'), 10);

      result.substrateIndex = substrateId;
      hoveredMol = substrateMols[substrateId];
      indexToMap = substratesToMap[substrateId];
      mapToOtherSide = mapToProduct;
    } else if (prodElem !== null) {
      hoveredMol = productMol;
      indexToMap = productToMap;
      mapToOtherSide = mapToSubstrates;
    } else {
      return result;
    }

    const molEvent = extractMolEvent(e);
    if (molEvent && hoveredMol) {
      let smallestDist = Number.POSITIVE_INFINITY;
      for (const [key, position] of Object.entries(hoveredMol.positions)) {
        if (indexToMap[key] == null || mapToOtherSide[indexToMap[key]] == null) {
          continue;
        }

        if (indexToMap[key] && V4.dist(position, molEvent.svgPos) < smallestDist) {
          smallestDist = V4.dist(position, molEvent.svgPos);
          result.mapId = indexToMap[key];
        }
      }
    }

    if (result.substrateIndex == null && result.mapId != null) {
      const substrateLocation = mapToSubstrates[result.mapId];
      if (substrateLocation != null) {
        result.substrateIndex = substrateLocation[0];
      }
    }

    return result;
  }

  onShowPrices = smiles => {
    this.setState({
      priceSidebar: {
        isOpen: true,
        smiles,
      },
    });
  };

  onHidePrices = () => {
    this.setState({
      priceSidebar: {
        isOpen: false,
        smiles: '',
      },
    });
  };

  onPricesReady = priceMap => {
    this.setState({ priceMap });
  };

  getArrowElement = (type, compact) => {
    const arrows = {
      single: (
        <div
          className={cx(cls.sign, {
            [cls.compactSign]: compact,
          })}
          key="arrow-sign"
        >
          ⟵
        </div>
      ),
      multiple: (
        <div>
          <span className={cls.arrowCaption}>{`(${this.props.steps} steps)`}</span>
          <div
            className={cx(cls.sign, cls.multiple, {
              [cls.compactSign]: compact,
            })}
            key="arrow-sign"
          >
            ⟵
          </div>
        </div>
      ),
    };
    const arrow = arrows[type];
    return arrow;
  };

  buildPricing = smilesList => {
    const { selectedDatasets, availabilityTier } = this.props;
    const { priceMap } = this.state;

    const selectedDatasetsById = _.keyBy(selectedDatasets, 'id');

    return smilesList.map(smiles => {
      const prices = priceMap[smiles];

      if (!Array.isArray(prices)) {
        return [];
      }

      return _.chain(prices)
        .map(item => {
          const dataset = selectedDatasetsById[item.datasetId];

          const tierOk = item.availabilityTier <= availabilityTier;

          let supplierOk = false;
          if (!dataset) {
            supplierOk = false;
          } else if (dataset && Array.isArray(dataset.selected_suppliers)) {
            supplierOk = dataset.selected_suppliers.includes(item.supplierId);
          } else if (dataset && !Array.isArray(dataset.selected_suppliers)) {
            supplierOk = true;
          }

          const price = item.price ?? 0;
          const amount = item.amount ?? 1;

          return {
            ...item,
            price,
            amount,
            userPrice: Math.max(price, price / amount),
            tierOk,
            supplierOk,
          };
        })
        .sortBy(item => -item.userPrice)
        .sortBy(['supplierOk', 'tierOk'])
        .reverse()
        .value();
    });
  };

  render() {
    const {
      className,
      reagentClassName,
      product,
      substrates,
      toolbox,
      compact,
      drawSubstrates,
      showPrices,
    } = this.props;
    const { productMol, substrateMols, hover, priceSidebar, priceMap } = this.state;

    const smilesList = [product?.smiles, ...(substrates ?? []).map(s => s?.smiles)];
    const rxnIdx = smilesList.join('-');

    if (!productMol || !substrateMols || substrates.length !== substrateMols.length) {
      return (
        <div
          className={cx(cls.empty, className)}
          ref={ref => {
            this.ref = ref;
          }}
        >
          <Spinner />
        </div>
      );
    }

    const { drawnProductMol, drawnSubstrateMols } = this.drawMols();
    const { productCircle, substrateCircle } = this.hoverCircles();
    const hoveredSubstrateIndex = hover.substrateIndex;

    const [, ...substratePrices] = this.buildPricing(smilesList);

    const productElement = (
      <div className={cx(cls.reagent, REACTION_PRODUCT, reagentClassName)} key={product.smiles}>
        <DrawnMolecule
          idx={`${rxnIdx}_product`}
          className={reagentClassName}
          drawnMol={drawnProductMol}
          size={drawnProductMol.bbox.size}
          after={productCircle}
          toolbox={
            toolbox && (
              <Toolbox
                smiles={product.smiles}
                mol={drawnProductMol}
                onRedraw={this.recalculateProduct}
              />
            )
          }
          onMouseUp={e => this.props.onClickProduct(product.id, e)}
        />
      </div>
    );

    const arrowElement = this.getArrowElement(this.props.reactionArrowType, compact);

    const plusElement = props => (
      <div
        className={cx(cls.sign, {
          [cls.compactSign]: compact,
        })}
        {...props}
      >
        +
      </div>
    );

    let components;
    if (!drawSubstrates) {
      components = [productElement];
    } else {
      components = [
        productElement,
        arrowElement,
        ...substrates.map((substrate, index) => {
          const drawnSubstrateMol = drawnSubstrateMols[index];
          const substrateMol = substrateMols[index];
          const prices = substratePrices[index];

          return (
            <>
              {index > 0 ? plusElement({ key: `plus-before-${substrate.smiles}` }) : null}
              <div
                className={cx(cls.reagent, REACTION_SUBSTRATE, reagentClassName)}
                data-substrate-index={index}
                key={substrate.smiles}
              >
                <DrawnMolecule
                  idx={`${rxnIdx}_substrate-${index}`}
                  className={cx(
                    {
                      [cls.pointer]: substrate.hasSuccessingReactions,
                    },
                    reagentClassName
                  )}
                  drawnMol={drawnSubstrateMol}
                  size={drawnProductMol.bbox.size}
                  after={hoveredSubstrateIndex === index ? substrateCircle : null}
                  toolbox={
                    toolbox && (
                      <Toolbox
                        smiles={substrate.smiles}
                        mol={substrateMol}
                        onRedraw={this.recalculateSubstrate(index)}
                      />
                    )
                  }
                  onMouseUp={e => this.props.onClickSubstrate(substrate.id, e)}
                  key={`substrate-${substrate.smiles}`}
                />
                <div className={cls.emoleculesLinkContainer}>
                  {prices?.length > 0 && (
                    <>
                      {prices[0].supplierOk && prices[0].tierOk && (
                        <Button
                          className={cls.emoleculesLink}
                          large
                          minimal
                          intent={Intent.SUCCESS}
                          text={
                            prices[0].price !== 0
                              ? `From $${prices[0].price} for ${prices[0].amount}g`
                              : 'Available at selected suppliers'
                          }
                          icon={prices[0].price !== 0 ? 'shopping-cart' : 'tick'}
                          onClick={this.onShowPrices.bind(null, substrate.smiles)}
                        />
                      )}

                      {prices[0].supplierOk && !prices[0].tierOk && (
                        <Button
                          className={cls.emoleculesLink}
                          large
                          minimal
                          intent={Intent.WARNING}
                          text="Available only in higher tiers"
                          icon="shopping-cart"
                          onClick={this.onShowPrices.bind(null, substrate.smiles)}
                        />
                      )}

                      {!prices[0].supplierOk && prices[0].tierOk && (
                        <Button
                          className={cls.emoleculesLink}
                          large
                          minimal
                          intent={Intent.WARNING}
                          text="Available only at other suppliers"
                          icon="shopping-cart"
                          onClick={this.onShowPrices.bind(null, substrate.smiles)}
                        />
                      )}

                      {!prices[0].supplierOk && !prices[0].tierOk && (
                        <Button
                          className={cls.emoleculesLink}
                          large
                          minimal
                          intent={Intent.WARNING}
                          text="Available only at other suppliers in higher tiers"
                          icon="shopping-cart"
                          onClick={this.onShowPrices.bind(null, substrate.smiles)}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          );
        }),
      ];
    }

    return (
      <>
        <div
          className={cx(cls.reaction, className)}
          ref={ref => {
            this.ref = ref;
          }}
          key="compounds"
          data-test="reaction"
        >
          {components}
        </div>
        {showPrices && (
          <>
            <PriceQuery smilesList={smilesList} onPricesReady={this.onPricesReady} />
            <PriceSidebar
              priceItems={priceMap[priceSidebar.smiles]}
              isOpen={priceSidebar.isOpen}
              selectedDatasets={this.props.selectedDatasets}
              onClose={this.onHidePrices}
            />
          </>
        )}
      </>
    );
  }
}
